import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { SUPPORTED_LANGS } from "../../routes/LangLayout";
import "./LanguageSelector.scss";

// react-country-flag renders SVG flags — plain flag emoji fall back to a
// bare two-letter country code on Windows browsers (no flag glyphs in the
// default system font), so we render actual SVGs instead for reliability.
const LANGUAGES = [
  { code: "fr", label: "Français", country: "FR" },
  { code: "en", label: "English",  country: "GB" },
  { code: "ar", label: "العربية",  country: "MA" },
  { code: "es", label: "Español",  country: "ES" },
];

const LanguageSelector = ({ className = "" }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  // Menu defaults to opening below the toggle — fine near the top of a page, but
  // inside something like the register form's mobile footer (little room below,
  // and no scroll container to reveal more) it gets clipped by the viewport edge.
  // Flip it above the toggle instead whenever there isn't enough room below.
  const [openUpward, setOpenUpward] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next && ref.current) {
        const rect = ref.current.getBoundingClientRect();
        const estimatedMenuHeight = LANGUAGES.length * 40 + 16;
        setOpenUpward(window.innerHeight - rect.bottom < estimatedMenuHeight);
      }
      return next;
    });
  };

  // On a /:lang/... public page the URL is the source of truth for the
  // active language (see LangLayout) — each option is a real <a href> to the
  // same path with just that first segment swapped (/en/help/stock -> /fr/help/stock),
  // so switching does a full page load: the prerendered HTML for that language
  // and its fonts (e.g. the Arabic font + RTL stylesheet) come in fresh, rather
  // than a client-side route change patching the current document.
  // Everywhere else (the authenticated app), it stays a plain in-place
  // language change with no routing implications.
  const pathSegments = location.pathname.split('/');
  const isLangRoute = SUPPORTED_LANGS.includes(pathSegments[1]);

  const hrefFor = (code) => {
    const segments = [...pathSegments];
    segments[1] = code;
    return `${segments.join('/')}${location.search}${location.hash}`;
  };

  const selectLanguage = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className={`syd-lang${className ? ` ${className}` : ""}`} ref={ref}>
      <button
        type="button"
        className="syd-lang__toggle"
        onClick={toggleOpen}
        aria-expanded={open}
      >
        <ReactCountryFlag countryCode={current.country} svg className="syd-lang__flag" aria-label={current.label} />
        <span className="syd-lang__code">{current.code.toUpperCase()}</span>
        <FontAwesomeIcon icon={faChevronDown} className="syd-lang__caret" />
      </button>

      {open && (
        <ul className={`syd-lang__menu${openUpward ? " syd-lang__menu--up" : ""}`}>
          {LANGUAGES.map(({ code, label, country }) => (
            <li key={code}>
              {isLangRoute ? (
                <a
                  href={hrefFor(code)}
                  hrefLang={code}
                  className={`syd-lang__option${code === current.code ? " syd-lang__option--active" : ""}`}
                >
                  <ReactCountryFlag countryCode={country} svg className="syd-lang__flag" aria-label={label} />
                  <span>{label}</span>
                </a>
              ) : (
                <button
                  type="button"
                  className={`syd-lang__option${code === current.code ? " syd-lang__option--active" : ""}`}
                  onClick={() => selectLanguage(code)}
                >
                  <ReactCountryFlag countryCode={country} svg className="syd-lang__flag" aria-label={label} />
                  <span>{label}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSelector;
